import {
  HEARTBEAT_INTERVAL_MS,
  LEASE_EXPIRY_MS,
} from "../../../shared/runLease";
import { useNow } from "../../hooks/useNow";
import { type StatusState } from "../../lib/chatStatus";
import { Text } from "../Text";

export const statusText = {
  waiting: "3B needs you",
  building: "3B is building",
  thinking: "3B is thinking",
  loading: "3B is connecting",
  resting: "What do you want to do?",
  dragging: "Let’s take a look",
} as const;

const planModeStatusText = {
  waiting: "3B needs your input",
  building: "3B is drafting a plan",
  thinking: "3B is planning",
  loading: "3B is gathering context",
  resting: "What should we plan?",
  dragging: "Let’s take a look",
} as const;

export const activeStates: StatusState[] = [
  "thinking",
  "building",
  "loading",
  "waiting",
];

function getStatusDisplayText(
  statusState: StatusState,
  elapsedMs: number,
  planMode: boolean
): string {
  if (statusState === "thinking") {
    if (planMode) {
      if (elapsedMs > 30_000) return "3B is refining the plan";
      if (elapsedMs > 15_000) return "3B is thinking through the plan";
    } else {
      if (elapsedMs > 30_000) return "3B is deep in thought";
      if (elapsedMs > 15_000) return "3B is pondering";
    }
  }
  return planMode ? planModeStatusText[statusState] : statusText[statusState];
}

// The status label is time-dependent (elapsed-time phrasing, and the
// reconnecting override below), and a dead server produces no re-renders,
// so the label ticks on its own clock.
export function StatusLabel({
  statusState,
  respondingAt,
  lastLoopHeartbeatAt,
  planMode,
}: {
  statusState: StatusState;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null | undefined;
  planMode: boolean;
}) {
  const now = useNow(1_000);
  // A running turn heartbeats every few seconds; three missed beats — one
  // shy of the takeover threshold — means its server died and another is
  // about to claim and resume the run.
  const heartbeatAt = Math.max(respondingAt ?? 0, lastLoopHeartbeatAt ?? 0);
  if (
    respondingAt != null &&
    statusState !== "waiting" &&
    statusState !== "resting" &&
    now - heartbeatAt > LEASE_EXPIRY_MS - HEARTBEAT_INTERVAL_MS
  ) {
    return <>3B is reconnecting</>;
  }
  return (
    <>
      {getStatusDisplayText(
        statusState,
        respondingAt ? now - respondingAt : 0,
        planMode
      )}
    </>
  );
}

function formatStatusTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((ms - totalSeconds * 1000) / 100);
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, "0")}.${tenths}s`;
  }
  return `${seconds}.${tenths}s`;
}

export function StatusTimer({ respondingAt }: { respondingAt: number }) {
  const now = useNow("frame");
  return (
    <Text variant="text-small" color="var(--text-secondary)">
      {formatStatusTime(Math.max(0, now - respondingAt))}
    </Text>
  );
}
